import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { api } from '../services/api';
import { countNeighborsScanFailures } from '../utils/neighborsScanUtils';
import { formatAgeSeconds } from './treeOptimization/NeighborDataStatus';

// =============================================================================
// NeighborsReportsList
//
// Browse-style table of every jnet2 Node that can produce a neighbour
// report. Excluded entirely:
//
//   * **CAPs** — AP-only radios, no STA radio to scan with. They are
//     observed indirectly via their children's reports.
//   * **AI devices** (Jetson, qrb*) and any other non-jnet2 family —
//     they have no STA radio at all, so listing them would falsely
//     mark them as "no report".
//
// For each remaining device the row shows the most recent neighbour-
// scan report timestamp from /tree-mgmt/analysis/neighbors-data-status:
//
//   * Devices with no report on file are highlighted in red so the
//     operator can spot gaps at a glance.
//   * Stale (>7 day) reports are highlighted amber.
//   * Clicking a row opens the per-device Neighbours Scan Report page
//     in a new tab, matching the behaviour of the "Neighbors Report"
//     button on the device map.
// =============================================================================

// AI device families have no STA radio so they never produce neighbour
// reports. The backend already excludes them from /neighbors-data-status, but we
// defend on the client too in case of version skew.
const isAIModel = (model) => {
  if (!model) return false;
  const m = model.toLowerCase();
  return m.includes('nvidia')
    || m.includes('jetson')
    || m.includes('qrb');
};

const isAICompatible = (compatible) => {
  if (!compatible) return false;
  const c = compatible.toLowerCase();
  return c.includes('jetson') || c.includes('qrb');
};

const formatTimestamp = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const statusOrder = (entry) => {
  if (!entry.has_data) return 0; // missing first
  if (entry.stale) return 1;     // then stale
  return 2;                      // then fresh
};

const NeighborsReportsList = ({ devices, allowNeighborsScan = true }) => {
  const [statusEntries, setStatusEntries] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [sortCol, setSortCol] = useState('status');
  const [sortDir, setSortDir] = useState('asc');
  const [reportType, setReportType] = useState('last');
  const [markGoldenDialogOpen, setMarkGoldenDialogOpen] = useState(false);
  const [markingGolden, setMarkingGolden] = useState(false);
  const [selectedSerials, setSelectedSerials] = useState(() => new Set());
  const [scanConfirmDialogOpen, setScanConfirmDialogOpen] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanInterval, setScanInterval] = useState(15);
  const [allFreq, setAllFreq] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleSort = useCallback((col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }, [sortCol]);

  const handleMarkGolden = useCallback(async () => {
    setMarkingGolden(true);
    try {
      await api.markNeighborsGolden(null);
      setMarkGoldenDialogOpen(false);
    } catch (err) {
      // keep dialog open so user sees the failure via the error state
    } finally {
      setMarkingGolden(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAnalysisDataStatus(reportType === 'golden');
      const entries = Array.isArray(data?.devices) ? data.devices : [];
      // Per spec: surface jnet2 Nodes only. CAPs are flagged is_cap on
      // the server side and are excluded from completeness anyway, so
      // hiding them keeps the list focused on devices that *should*
      // have a report.
      const filtered = entries.filter((e) => !e.is_cap);
      setStatusEntries(filtered);
      setGeneratedAt(data?.generated_at || null);
    } catch (err) {
      setError(err?.message || 'Failed to load neighbour data status');
      setStatusEntries([]);
    } finally {
      setLoading(false);
    }
  }, [reportType]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Build a serial → device metadata lookup from the devices prop so
  // we can show friendly names alongside serials and decide whether
  // a row belongs in the neighbour-report list at all (jnet2 Nodes
  // only — CAPs and AI families are excluded).
  const deviceLookup = useMemo(() => {
    const map = new Map();
    (devices || []).forEach((d) => {
      if (!d?.serial_number) return;
      map.set(d.serial_number.toLowerCase(), d);
    });
    return map;
  }, [devices]);

  const rows = useMemo(() => {
    const enriched = statusEntries.map((entry) => {
      const dev = deviceLookup.get(entry.serial?.toLowerCase());
      return {
        ...entry,
        model: dev?.capabilities?.model || '',
        compatible: dev?.compatible || dev?.capabilities?.compatible || '',
      };
    });

    // Defence-in-depth: drop any AI / non-jnet2 row even if the
    // backend hadn't been updated to filter them server-side.
    const meshOnly = enriched.filter((entry) => {
      if (isAIModel(entry.model)) return false;
      if (isAICompatible(entry.compatible)) return false;
      // If we have device metadata and it's explicitly non-jnet2, drop
      // it. Entries we couldn't join (no metadata) are kept — better
      // to show an unidentified row than silently hide it.
      if (entry.compatible && entry.compatible.toLowerCase() !== 'jnet2') {
        return false;
      }
      return true;
    });

    const trimmed = filter.trim().toLowerCase();
    const filtered = trimmed
      ? meshOnly.filter((entry) =>
          entry.serial?.toLowerCase().includes(trimmed)
          || entry.name?.toLowerCase().includes(trimmed),
        )
      : meshOnly;

    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered.sort((a, b) => {
      switch (sortCol) {
        case 'serial':
          return dir * (a.serial || '').localeCompare(b.serial || '');
        case 'connection':
          return dir * ((a.connected ? 1 : 0) - (b.connected ? 1 : 0));
        case 'status': {
          const oa = statusOrder(a);
          const ob = statusOrder(b);
          if (oa !== ob) return dir * (oa - ob);
          return (a.serial || '').localeCompare(b.serial || '');
        }
        case 'bands': {
          const ba = [...(a.bands || [])].sort().join(',');
          const bb = [...(b.bands || [])].sort().join(',');
          return dir * ba.localeCompare(bb);
        }
        case 'lastReport': {
          const ta = a.last_updated ? new Date(a.last_updated).getTime() : 0;
          const tb = b.last_updated ? new Date(b.last_updated).getTime() : 0;
          return dir * (ta - tb);
        }
        case 'age': {
          const aa = a.has_data && a.age_seconds != null ? a.age_seconds : Infinity;
          const ab = b.has_data && b.age_seconds != null ? b.age_seconds : Infinity;
          return dir * (aa - ab);
        }
        default:
          return 0;
      }
    });
  }, [statusEntries, deviceLookup, filter, sortCol, sortDir]);

  const summary = useMemo(() => {
    let missing = 0;
    let stale = 0;
    let fresh = 0;
    rows.forEach((r) => {
      if (!r.has_data) missing += 1;
      else if (r.stale) stale += 1;
      else fresh += 1;
    });
    return { missing, stale, fresh, total: rows.length };
  }, [rows]);

  const selectableRows = useMemo(
    () => rows.filter((r) => r.connected && r.serial),
    [rows],
  );

  const selectedCount = selectedSerials.size;
  const allSelectableSelected = selectableRows.length > 0
    && selectableRows.every((r) => selectedSerials.has(r.serial));
  const someSelectableSelected = selectableRows.some((r) => selectedSerials.has(r.serial));

  const toggleRowSelection = useCallback((serial, connected) => {
    if (!serial || !connected) return;
    setSelectedSerials((prev) => {
      const next = new Set(prev);
      if (next.has(serial)) {
        next.delete(serial);
      } else {
        next.add(serial);
      }
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedSerials((prev) => {
      if (allSelectableSelected) {
        const next = new Set(prev);
        selectableRows.forEach((r) => next.delete(r.serial));
        return next;
      }
      const next = new Set(prev);
      selectableRows.forEach((r) => next.add(r.serial));
      return next;
    });
  }, [allSelectableSelected, selectableRows]);

  const handleRunNeighborsScan = useCallback(async () => {
    setScanConfirmDialogOpen(false);
    const serials = [...selectedSerials];
    if (serials.length === 0) {
      setSnackbarMessage('No devices selected for neighbors scan');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setScanLoading(true);
    try {
      const result = await api.startNeighborsScanBatch(serials, scanInterval, allFreq);
      const failedCount = countNeighborsScanFailures(result.results);
      if (failedCount > 0) {
        setSnackbarMessage(`Neighbors scan: ${result.commands_sent} successful, ${failedCount} failed`);
        setSnackbarSeverity('warning');
      } else {
        setSnackbarMessage(`Neighbors scan initiated for ${serials.length} device${serials.length > 1 ? 's' : ''}`);
        setSnackbarSeverity('success');
      }
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(`Failed to start neighbors scan: ${err?.message || 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setScanLoading(false);
    }
  }, [selectedSerials, scanInterval, allFreq]);

  const openReport = useCallback((serial) => {
    if (!serial) return;
    const qs = allowNeighborsScan && reportType === 'golden' ? '?golden=true' : '';
    window.open(`/neighbors/${serial}${qs}`, '_blank', 'noopener,noreferrer');
  }, [allowNeighborsScan, reportType]);

  const renderStatus = (entry) => {
    if (!entry.has_data) {
      return (
        <Chip
          size="small"
          color="error"
          icon={<ErrorOutlineIcon />}
          label="No report"
        />
      );
    }
    if (entry.stale) {
      return (
        <Chip
          size="small"
          color="warning"
          icon={<WarningAmberIcon />}
          label="Stale"
        />
      );
    }
    return (
      <Chip
        size="small"
        color="success"
        icon={<CheckCircleOutlineIcon />}
        label="Fresh"
      />
    );
  };

  const renderConnection = (entry) => (
    <Chip
      size="small"
      variant="outlined"
      color={entry.connected ? 'success' : 'default'}
      label={entry.connected ? 'Connected' : 'Disconnected'}
    />
  );

  const rowSx = (entry) => {
    if (!entry.has_data) {
      return {
        backgroundColor: (theme) => (theme.palette.mode === 'dark'
          ? 'rgba(244, 67, 54, 0.18)'
          : 'rgba(244, 67, 54, 0.10)'),
        '&:hover': {
          backgroundColor: (theme) => (theme.palette.mode === 'dark'
            ? 'rgba(244, 67, 54, 0.30)'
            : 'rgba(244, 67, 54, 0.18)'),
        },
      };
    }
    if (entry.stale) {
      return {
        backgroundColor: (theme) => (theme.palette.mode === 'dark'
          ? 'rgba(255, 152, 0, 0.16)'
          : 'rgba(255, 152, 0, 0.10)'),
        '&:hover': {
          backgroundColor: (theme) => (theme.palette.mode === 'dark'
            ? 'rgba(255, 152, 0, 0.28)'
            : 'rgba(255, 152, 0, 0.18)'),
        },
      };
    }
    return {
      '&:hover': {
        backgroundColor: 'action.hover',
      },
    };
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'flex-start' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Neighbours Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Most recent neighbour-scan report timestamp for every jnet2
            Node. Devices with no report on file are highlighted in red.
            CAPs are excluded (AP-only radios — observed indirectly via
            their children's reports); AI devices (Jetson, qrb*) and any
            other non-jnet2 family are excluded too because they have no
            STA radio and never produce a neighbour report.
          </Typography>
          {generatedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Snapshot taken {formatTimestamp(generatedAt)}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {allowNeighborsScan ? (
            <ToggleButtonGroup
              value={reportType}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) setReportType(v); }}
            >
              <ToggleButton value="last">Last</ToggleButton>
              <ToggleButton value="golden">Golden</ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <Chip size="small" variant="outlined" label="Last reports" />
          )}
          <Chip size="small" color="error" variant={summary.missing ? 'filled' : 'outlined'} label={`${summary.missing} missing`} />
          <Chip size="small" color="warning" variant={summary.stale ? 'filled' : 'outlined'} label={`${summary.stale} stale`} />
          <Chip size="small" color="success" variant="outlined" label={`${summary.fresh} fresh`} />
          <Chip size="small" variant="outlined" label={`${summary.total} total`} />
          {allowNeighborsScan && (
            <>
              <Tooltip title={selectedCount === 0 ? 'Select connected devices to scan' : `Run scan on ${selectedCount} selected device${selectedCount > 1 ? 's' : ''}`}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={scanLoading ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                    onClick={() => setScanConfirmDialogOpen(true)}
                    disabled={scanLoading || selectedCount === 0}
                  >
                    {scanLoading ? 'Scanning…' : `Run Scan${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Mark last reports as golden for all devices">
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => setMarkGoldenDialogOpen(true)}
                >
                  Mark as Golden
                </Button>
              </Tooltip>
            </>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchStatus} disabled={loading} size="small">
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ mt: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Filter by serial or name"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          fullWidth
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={fetchStatus}>Retry</Button>
        }>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {allowNeighborsScan && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={someSelectableSelected && !allSelectableSelected}
                    checked={allSelectableSelected}
                    disabled={selectableRows.length === 0}
                    onChange={toggleSelectAllVisible}
                    inputProps={{ 'aria-label': 'Select all connected devices in view' }}
                  />
                </TableCell>
              )}
              {[
                { id: 'serial', label: 'Serial' },
                { id: 'connection', label: 'Connection' },
                { id: 'status', label: 'Status' },
                { id: 'bands', label: 'Bands' },
                { id: 'lastReport', label: 'Last Report' },
                { id: 'age', label: 'Age' },
              ].map(({ id, label }) => (
                <TableCell key={id} sortDirection={sortCol === id ? sortDir : false}>
                  <TableSortLabel
                    active={sortCol === id}
                    direction={sortCol === id ? sortDir : 'asc'}
                    onClick={() => handleSort(id)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right">Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={allowNeighborsScan ? 8 : 7}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No jnet2 Nodes match the current filter.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((entry) => (
              <TableRow
                key={entry.serial}
                hover
                onClick={() => openReport(entry.serial)}
                selected={selectedSerials.has(entry.serial)}
                sx={{ cursor: 'pointer', ...rowSx(entry) }}
              >
                {allowNeighborsScan && (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={entry.connected ? 'Select for scan' : 'Device must be connected to scan'}>
                      <span>
                        <Checkbox
                          size="small"
                          checked={selectedSerials.has(entry.serial)}
                          disabled={!entry.connected}
                          onChange={() => toggleRowSelection(entry.serial, entry.connected)}
                          inputProps={{ 'aria-label': `Select ${entry.serial}` }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell sx={{ fontFamily: 'monospace' }}>{entry.serial}</TableCell>
                <TableCell>{renderConnection(entry)}</TableCell>
                <TableCell>{renderStatus(entry)}</TableCell>
                <TableCell>
                  {entry.bands && entry.bands.length > 0 ? (
                    <Stack direction="row" spacing={0.5}>
                      {entry.bands.map((b) => (
                        <Chip key={b} label={b} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>{formatTimestamp(entry.last_updated)}</TableCell>
                <TableCell>
                  {entry.has_data && entry.age_seconds != null
                    ? `${formatAgeSeconds(entry.age_seconds)} ago`
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Open Neighbours Scan Report">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openReport(entry.serial);
                      }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={markGoldenDialogOpen} onClose={() => setMarkGoldenDialogOpen(false)}>
        <DialogTitle>Mark Last Reports as Golden</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will copy the current <strong>last</strong> neighbors report for every device
            into the golden slot. Any previously marked golden report will be
            <strong> overwritten</strong>. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkGoldenDialogOpen(false)} disabled={markingGolden}>
            Cancel
          </Button>
          <Button
            onClick={handleMarkGolden}
            color="warning"
            variant="contained"
            disabled={markingGolden}
            startIcon={markingGolden ? <CircularProgress size={14} /> : null}
          >
            {markingGolden ? 'Marking…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scanConfirmDialogOpen}
        onClose={() => !scanLoading && setScanConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Neighbors Scan</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Run a neighbors scan on {selectedCount} selected connected device{selectedCount > 1 ? 's' : ''}.
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              type="number"
              label="Scan Interval (seconds)"
              value={scanInterval}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setScanInterval(value);
                } else {
                  const numValue = parseInt(value, 10);
                  if (!Number.isNaN(numValue)) {
                    setScanInterval(numValue);
                  }
                }
              }}
              onBlur={() => {
                if (!scanInterval || scanInterval < 1) {
                  setScanInterval(15);
                }
              }}
              inputProps={{ min: 1, max: 60 }}
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel>All Frequencies</InputLabel>
              <Select
                value={allFreq}
                onChange={(e) => setAllFreq(e.target.value)}
                label="All Frequencies"
              >
                <MenuItem value={true}>Yes — scan all bands</MenuItem>
                <MenuItem value={false}>No — known frequencies only</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <DialogContentText sx={{ mt: 2 }}>
            The scan runs in the background. Results appear here once devices complete scanning.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanConfirmDialogOpen(false)} disabled={scanLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleRunNeighborsScan}
            variant="contained"
            disabled={scanLoading}
            startIcon={scanLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {scanLoading ? 'Starting…' : 'Start Scan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default NeighborsReportsList;
